import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.ts';

export const api: AxiosInstance = axios.create({ baseURL: '/api', withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t && cfg.headers) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

type Recovery = 'recovered' | 'dead' | 'transient';

// Classify why the current session couldn't be refreshed:
//  - recovered: a fresh token (or ambient SSO) is now in place → retry the request.
//  - dead:      the refresh token is genuinely revoked/expired → end the session.
//  - transient: network blip / 5xx → keep the session; the request just fails.
// NEVER log out on 'transient' — that was the cause of random mid-session logouts.
async function recoverSession(): Promise<Recovery> {
  try {
    await useAuthStore.getState().refresh();
    return 'recovered';
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      // Refresh token genuinely revoked/expired — try the ambient wap_sso cookie
      // before giving up. ssoBootstrap swallows its own errors and returns bool.
      const ok = await useAuthStore.getState().ssoBootstrap(true);
      return ok ? 'recovered' : 'dead';
    }
    return 'transient';
  }
}

let refreshing: Promise<Recovery> | null = null;
api.interceptors.response.use(
  (r) => r,
  async (err: { response?: { status?: number }; config?: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
    const status = err.response?.status;
    const original = err.config;
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= recoverSession().finally(() => { refreshing = null; });
      const outcome = await refreshing;
      if (outcome === 'recovered') return api(original);
      if (outcome === 'dead') {
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') window.location.href = '/login';
      }
      // 'transient' → fall through and reject; the session stays signed in.
    }
    throw err;
  },
);
